import { Button } from '@/components/ui/button.tsx'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { useCallback, useMemo } from 'react'

import { Divider } from '@/components/dividers/Divider.tsx'
import { EmptySection } from '@/components/dividers/EmptySection.tsx'
import { SectionDividerLabel } from '@/components/dividers/SectionDividerLabel.tsx'
import { DRAFT_CONTAINER, IPM_CONTAINER, PRE_IPM_CONTAINER } from '@/constants/CONTAINER_NAMES.ts'
import { NEW_STORY_ID } from '@/constants/DEFAULT_VALUES.ts'
import { STORY_SUBGROUPS } from '@/constants/STORY_SUBGROUPS.ts'
import { getStory } from '@/requests/apollo/cache.ts'
import { useCreateStory } from '@/requests/mutations/useCreateStory.ts'
import { useQueryStories } from '@/requests/queries/useQueryProject.ts'
import { chainFunctions } from '@/utils/call.ts'
import { GlobalPubSub } from '@/utils/pubsub.ts'
import { scrollToStory } from '@/utils/scroll.ts'

type Props = {
  filter?: (stories: Story[]) => Story[]
}

export const IceboxPanel = ({ filter }: Props) => {
  const { stories } = useQueryStories('unscheduled')
  const { createCache: createStoryCache } = useCreateStory(NEW_STORY_ID)

  const filtered = useMemo(() => (filter ? filter(stories) : stories), [stories, filter])

  const ipmStories = useMemo(() => filtered.filter((story) => story.subgroup === STORY_SUBGROUPS.IPM), [filtered])
  const preIpmStories = useMemo(
    () => filtered.filter((story) => story.subgroup === STORY_SUBGROUPS.PRE_IPM),
    [filtered],
  )
  const draftStories = useMemo(() => {
    const result = filtered.filter((story) => !story.subgroup || story.subgroup === STORY_SUBGROUPS.DRAFT)
    if (result[result.length - 1]?.id === NEW_STORY_ID) result.unshift(result.pop() as Story)
    return result
  }, [filtered])

  const onNewStoryCreate = useCallback(() => {
    const newCachedStory = getStory(NEW_STORY_ID)

    if (newCachedStory) {
      scrollToStory(NEW_STORY_ID, 'smooth')
      return newCachedStory as Story
    }

    chainFunctions(
      () => createStoryCache(NEW_STORY_ID),
      () => GlobalPubSub.publish(GlobalPubSub.Signals.TOGGLE_STORY_FORM, NEW_STORY_ID, true),
      () => new Promise((resolve) => setTimeout(resolve, 10)),
      () => scrollToStory(NEW_STORY_ID, 'smooth'),
    )
  }, [createStoryCache])

  return (
    <div className="flex-1 flex flex-col gap-2 overflow-auto bg-surface-backgroundElevated rounded-xl p-2 pr-0.5 min-w-[520px]">
      <div className="flex flex-row justify-between items-center mr-1.5">
        <p className="font-EN-KR-heading3-bold text-contents-primary">Icebox</p>
        <Button
          type="button"
          variant="primary"
          className="h-8 font-EN-KR-body2-bold py-4 px-3 rounded-xs"
          onClick={onNewStoryCreate}
        >
          Add Story
        </Button>
      </div>
      <div className="flex-1 overflow-scroll tracker-scrollbar">
        <SortableContext items={stories} strategy={verticalListSortingStrategy}>
          <ul className="flex flex-col gap-2">
            <Section title="IPM" stories={ipmStories} sectionRefName={IPM_CONTAINER} />
            <Section title="Pre-IPM" stories={preIpmStories} sectionRefName={PRE_IPM_CONTAINER} />
            <Section title="Draft" stories={draftStories} sectionRefName={DRAFT_CONTAINER} />
          </ul>
        </SortableContext>
      </div>
    </div>
  )
}

const Section = ({ title, stories, sectionRefName }: { title: string; stories: Story[]; sectionRefName: string }) => (
  <>
    {stories.length === 0 ? (
      <EmptySection title={title} sectionRefName={sectionRefName} />
    ) : (
      <div className="overflow-hidden">
        <Divider uniqueKey={title} stories={stories} isDraggableStories>
          <SectionDividerLabel title={title} />
        </Divider>
      </div>
    )}
  </>
)
